import * as Sentry from "@sentry/browser";
import Signal from "../../signal";
import { Dashboard, Driver, Delivery, DeliveryGroup, DispatchTask, DeliveryTracking, DriverTrackingUpdate, DeliveryTrackingStep } from "./model";
import { DeliveryStatus } from "../delivery_status";
import {
  addAdminDispatchDashboardDriverPath,
  allocateAdminDispatchDashboardDeliveryPath,
  allocateAdminDispatchDashboardGroupPath,
  createNoteAdminDispatchDashboardDriverPath,
  deallocateAdminDispatchDashboardDeliveryPath,
  sendDelayMessageAdminDispatchDashboardDeliveryPath,
  delayPrepAdminDispatchDashboardDeliveryPath,
  updateDashNoteAdminDispatchDashboardDeliveryPath,
  groupAdminDispatchDashboardDeliveryPath,
  ungroupAdminDispatchDashboardDeliveryPath,
  lockAdminDispatchDashboardGroupPath,
  mergeAdminDispatchDashboardGroupPath,
  ungroupAdminDispatchDashboardGroupPath,
  removeAdminDispatchDashboardDriverPath,
  compactAdminDispatchDashboardGroupsPath,
  orderChronologicallyAdminDispatchDashboardGroupsPath,
  unlockAdminDispatchDashboardGroupPath,
  adminDispatchDashboardUpdatePanicPath,
  updateDispatcherAdminAreaPath,
  adminDispatchDashboardTriggerAutogroupingPath,
  adminDispatchDashboardCancelAutogroupingPath,
} from 'src/routes';
import { adminCable } from "src/admin_cable";
import { createContext, useContext } from "react";

interface DispatchDashboardChannelData {
  dashboard?: Dashboard;
  delivery?: unknown;
  group?: unknown;
  autogroupingStatus?: string;
  areaOpen?: string;
  areaClosed?: string;
  dispatcherId?: number | null;
  dispatcherName?: string | null;
  nextAvailablePickupTime?: string;
  driver?: unknown;
  dispatchTask?: unknown;
  debug?: string;
}

interface DispatchTrackingChannelData {
  driverId: number;
  receivedAt: string;
  latitude: number;
  longitude: number;
  heading?: number;
  deliveries: {
    deliveryUid: string;
    status: string;
    secondsToPickup?: number;
    routeToPickup?: DeliveryTrackingStep[];
    secondsToDropoff?: number;
    routeToDropoff?: DeliveryTrackingStep[];
  }[];
}

export interface IDispatchClient {
  areaId: number;
  csrfToken: string;
  dashboard?: Dashboard;
  connected: boolean;

  readonly dashboardConnected : Signal<boolean>;
  readonly dashboardUpdated : Signal<Dashboard>;
  readonly driverTrackingUpdated : Signal<DriverTrackingUpdate>;

  start() : void;
  stop() : void;

  updateDispatcher() : Promise<void>;
  updatePanic(panicTo: string) : Promise<void>;
  deliveryTrackingForDriver(driverId: number) : DeliveryTracking[];
  allocateDeliveryToDriver(deliveryId: string, driverId: number, fromDriverId: number | null) : Promise<void>;
  deallocateDeliveryFromDriver(deliveryId: string, fromDriverId: number) : Promise<void>;
  allocateDeliveryToGroup(deliveryId: string, groupId: number, fromDriverId: number | null) : Promise<void>;
  removeDeliveryFromGroup(deliveryId: string, groupId: number) : Promise<void>;
  allocateGroupToDriver(groupId: number, driverId: number) : Promise<void>;
  lockGroup(groupId: number) : Promise<void>;
  unlockGroup(groupId: number) : Promise<void>;
  mergeGroups(fromGroupId: number, toGroupId: number) : Promise<void>;
  unGroup(groupId: number) : Promise<void>;
  compactGroups() : Promise<void>;
  orderGroupsChronologically() : Promise<void>;
  triggerAutogrouping() : Promise<void>;
  cancelAutogrouping() : Promise<void>;
  sendDelayMessage(deliveryId: string, delay: string, reason: string) : Promise<void>;
  delayPrep(deliveryId: string, delay: string) : Promise<void>;
  updateDashNote(deliveryId: string, note: string) : Promise<void>;
  addDriver(driverId: number) : Promise<void>;
  removeDriver(driverId: number) : Promise<void>;
  createDriverNote(driverId: number, note: string) : Promise<void>;
}

export default class DispatchApiClient implements IDispatchClient {
  areaId: number;
  csrfToken: string;
  dashboard?: Dashboard;
  connected: boolean = false;
  deliveryTrackingByDriverId: Map<number, DeliveryTracking[]> = new Map();
  dashboardSubscription?: ActionCable.Subscription;
  trackingSubscription?: ActionCable.Subscription;
  dashboardConnectedAt?: number;
  dashboardDisconnectedAt?: number;
  dashboardReceivedDataAt?: number;
  trackingConnectedAt?: number;
  trackingDisconnectedAt?: number;
  trackingReceivedDataAt?: number;

  readonly dashboardConnected = new Signal<boolean>();
  readonly dashboardUpdated = new Signal<Dashboard>();
  readonly driverTrackingUpdated = new Signal<DriverTrackingUpdate>();

  constructor(areaId: number, csrfToken: string) {
    this.areaId = areaId;
    this.csrfToken = csrfToken;
  }

  start() : void {
    console.log("Subscribing to dispatch dashboard updates");
    this.subscribeToUpdates();
    document.addEventListener("visibilitychange", this.debugVisibilityChange);
    document.addEventListener("freeze", this.debugFreeze);
    document.addEventListener("resume", this.debugResume);
  }

  stop() : void {
    console.log("Unsubscribing from dispatch dashboard updates");
    this.unsubscribeFromUpdates();
    document.removeEventListener("resume", this.debugResume);
    document.removeEventListener("freeze", this.debugFreeze);
    document.removeEventListener("visibilitychange", this.debugVisibilityChange);
  }

  // Some more logging to help debug the lost-updates problems
  debugVisibilityChange(): void { console.log(`Document visibilityChange event, visibilityState now ${document.visibilityState}`); }
  debugFreeze(): void { console.log(`Document freeze event, wasDiscarded is ${document.wasDiscarded}`); }
  debugResume(): void { console.log(`Document resume event, wasDiscarded is now ${document.wasDiscarded}`); }

  /*
    Dashboard APIs
  */
  updateDispatcher(): Promise<void> {
    return this.post(
      updateDispatcherAdminAreaPath(this.areaId));
  }

  updatePanic(panicTo: string): Promise<void> {
    return this.post(
      adminDispatchDashboardUpdatePanicPath(this.areaId),
      { panicTo: panicTo });
  }

  allocateDeliveryToDriver(deliveryId: string, driverId: number, fromDriverId: number | null) : Promise<void> {
    return this.post(
      allocateAdminDispatchDashboardDeliveryPath(this.areaId, deliveryId),
      { driverId: driverId, fromDriverId: fromDriverId });
  }

  deallocateDeliveryFromDriver(deliveryId: string, fromDriverId: number) : Promise<void> {
    return this.post(
      deallocateAdminDispatchDashboardDeliveryPath(this.areaId, deliveryId),
      { fromDriverId: fromDriverId });
  }

  allocateDeliveryToGroup(deliveryId: string, groupId: number, fromDriverId: number | null) : Promise<void> {
    return this.post(
      groupAdminDispatchDashboardDeliveryPath(this.areaId, deliveryId),
      { groupId: groupId, fromDriverId: fromDriverId });
  }

  removeDeliveryFromGroup(deliveryId: string, groupId: number) : Promise<void> {
    return this.post(
      ungroupAdminDispatchDashboardDeliveryPath(this.areaId, deliveryId),
      { fromGroupId: groupId });
  }

  allocateGroupToDriver(groupId: number, driverId: number) : Promise<void> {
    return this.post(
      allocateAdminDispatchDashboardGroupPath(this.areaId, groupId),
      { driverId: driverId });
  }

  lockGroup(groupId: number): Promise<void> {
    return this.post(
      lockAdminDispatchDashboardGroupPath(this.areaId, groupId),
      null);
  }

  unlockGroup(groupId: number): Promise<void> {
    return this.post(
      unlockAdminDispatchDashboardGroupPath(this.areaId, groupId),
      null);
  }

  mergeGroups(fromGroupId: number, toGroupId: number): Promise<void> {
    return this.post(
      mergeAdminDispatchDashboardGroupPath(this.areaId, fromGroupId),
      { toGroupId: toGroupId });
  }

  unGroup(groupId: number): Promise<void> {
    return this.post(
      ungroupAdminDispatchDashboardGroupPath(this.areaId, groupId),
      null);
  }

  compactGroups(): Promise<void> {
    return this.post(
      compactAdminDispatchDashboardGroupsPath(this.areaId),
      null);
  }

  orderGroupsChronologically(): Promise<void> {
    return this.post(
      orderChronologicallyAdminDispatchDashboardGroupsPath(this.areaId),
      null);
  }

  triggerAutogrouping(): Promise<void> {
    return this.post(
      adminDispatchDashboardTriggerAutogroupingPath(this.areaId),
      null);
  }

  cancelAutogrouping(): Promise<void> {
    return this.post(
      adminDispatchDashboardCancelAutogroupingPath(this.areaId),
      null);
  }

  sendDelayMessage(deliveryId: string, delay: string, reason: string): Promise<void> {
    return this.post(
      sendDelayMessageAdminDispatchDashboardDeliveryPath(this.areaId, deliveryId),
      { delay: delay, reason: reason });
  }

  delayPrep(deliveryId: string, delay: string): Promise<void> {
    return this.post(
      delayPrepAdminDispatchDashboardDeliveryPath(this.areaId, deliveryId),
      { delayBy: delay });
  }

  updateDashNote(deliveryId: string, note: string): Promise<void> {
    return this.post(
      updateDashNoteAdminDispatchDashboardDeliveryPath(this.areaId, deliveryId),
      { note });
  }

  addDriver(driverId: number): Promise<void> {
    return this.post(
      addAdminDispatchDashboardDriverPath(this.areaId, driverId),
      null);
  }

  removeDriver(driverId: number): Promise<void> {
    return this.post(
      removeAdminDispatchDashboardDriverPath(this.areaId, driverId),
      null);
  }

  createDriverNote(driverId: number, note: string): Promise<void> {
    return this.post(
      createNoteAdminDispatchDashboardDriverPath(this.areaId, driverId),
      { note: note });
  }

  /*
    API primitives
  */
  private async getJson<T = unknown>(path: string): Promise<T> {
    return fetch(path, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    }).then((resp) => {
      if (resp.status > 299) throw new Error("Bad HTTP status");
      return resp.json();
    });
  }

  private async post<T = unknown>(path: string, body?: T): Promise<void> {
    return fetch(path, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
       },
      body: body ? JSON.stringify(body) : "",
    }).then((resp) => {
      if (resp.status > 299) throw new Error("Bad HTTP status");
    });
  }

  /*
    ActionCable updates
  */
  subscribeToUpdates() : void {
    this.dashboardSubscription = adminCable().subscriptions.create({ channel: "DispatchDashboardChannel", area_id: this.areaId }, {
      received: (data: DispatchDashboardChannelData) => {
        if (!data.debug) this.dashboardReceivedDataAt = Date.now();
        this.onDispatchDashboardChannelData(data);
      },
      disconnected: () => {
        this.connected = false;
        this.dashboardDisconnectedAt = Date.now();
        if (this.dashboardConnectedAt) {
          console.log("Disconnected from dispatch dashboard updates", { duration: (this.dashboardDisconnectedAt - this.dashboardConnectedAt) });
        }
        this.dashboardConnected.notifyListeners(false);
      },
      connected: () => {
        this.connected = true;
        this.dashboardConnectedAt = Date.now();
        if (this.dashboardDisconnectedAt) {
          console.log(`Reconnected to dispatch dashboard updates`, { duration: (this.dashboardConnectedAt - this.dashboardDisconnectedAt) });
        } else {
          console.log("Connected to dispatch dashboard updates");
        }
        this.dashboardConnected.notifyListeners(true);
      }
    });

    this.trackingSubscription = adminCable().subscriptions.create({ channel: "DispatchTrackingChannel", area_id: this.areaId }, {
      received: (data: DispatchTrackingChannelData) => {
        this.trackingReceivedDataAt = Date.now();
        this.onDispatchTrackingChannelData(data);
      },
      disconnected: () => {
        this.trackingDisconnectedAt = Date.now();
      },
      connected: () => {
        this.trackingConnectedAt = Date.now();
      }
    });
  }

  unsubscribeFromUpdates() : void {
    this.dashboardSubscription?.unsubscribe();
    this.dashboardSubscription = undefined;
    this.trackingSubscription?.unsubscribe();
    this.trackingSubscription = undefined;
  }

  private updateOneRecord<T>(oldRecords: (T & { id: number | string })[], newRecord: (T & { id: number | string })): T[] {
    const newRecords = oldRecords.map(oldRecord => {
      if (oldRecord.id == newRecord.id) {
        return newRecord;
      } else {
        return oldRecord;
      }
    });

    if (!newRecords.includes(newRecord)) {
      newRecords.push(newRecord);
    }

    return newRecords;
  }

  private async onDispatchDashboardChannelData(data: DispatchDashboardChannelData) {
    if (data.dashboard != undefined) {
      const decodedDashboard = Dashboard.decoder.decode(data.dashboard);
      if (!decodedDashboard.isOk()) throw new Error(decodedDashboard.error);
      const updatedDashboard = decodedDashboard.value;

      this.dashboard = updatedDashboard;
    }

    if (this.dashboard && data.delivery != undefined) {
      const decodedDelivery = Delivery.decoder.decode(data.delivery);
      if (!decodedDelivery.isOk()) throw new Error(decodedDelivery.error);
      const updatedDelivery = decodedDelivery.value;

      const updatedDeliveries = updatedDelivery.deliveryStatus != DeliveryStatus.DropoffComplete && updatedDelivery.deliveryStatus != DeliveryStatus.ReturnComplete && updatedDelivery.deliveryStatus != DeliveryStatus.Cancelled ?
        this.updateOneRecord(this.dashboard.deliveries, updatedDelivery) :
        this.dashboard.deliveries.filter((d) => d.id != updatedDelivery.id);
      this.dashboard = this.dashboard.withChanges({ deliveries: updatedDeliveries });
    }

    if (this.dashboard && data.group != undefined) {
      const decodedDeliveryGroup = DeliveryGroup.decoder.decode(data.group);
      if (!decodedDeliveryGroup.isOk()) throw new Error(decodedDeliveryGroup.error);
      const updatedDeliveryGroup = decodedDeliveryGroup.value;

      const updatedDeliveryGroups = this.updateOneRecord(this.dashboard.groups, updatedDeliveryGroup);
      updatedDeliveryGroups.sort((a, b) => a.groupNumber - b.groupNumber);
      this.dashboard = this.dashboard.withChanges({ groups: updatedDeliveryGroups });
    }

    if (this.dashboard && data.areaOpen != undefined) {
      this.dashboard = this.dashboard.withChanges({ areaOpen: new Date(data.areaOpen) });
    }

    if (this.dashboard && data.areaClosed != undefined) {
      this.dashboard = this.dashboard.withChanges({ areaClosed: new Date(data.areaClosed) });
    }

    if (this.dashboard && data.dispatcherId != undefined) {
      this.dashboard = this.dashboard.withChanges({ dispatcherId: data.dispatcherId });
    }
    if (this.dashboard && data.dispatcherName != undefined) {
      this.dashboard = this.dashboard.withChanges({ dispatcherName: data.dispatcherName });
    }

    if (this.dashboard && data.nextAvailablePickupTime != undefined) {
      const newNextAvailablePickupTime = new Date(data.nextAvailablePickupTime);
      this.dashboard = this.dashboard.withChanges({ nextAvailablePickupTime: newNextAvailablePickupTime });
    }

    if (this.dashboard && data.driver != undefined) {
      const decodedDriver = Driver.decoder.decode(data["driver"]);
      if (!decodedDriver.isOk()) throw new Error(decodedDriver.error);
      const updatedDriver = decodedDriver.value;

      const updatedDrivers = updatedDriver.areaId == this.areaId ?
        this.updateOneRecord(this.dashboard.drivers, updatedDriver) :
        this.dashboard.drivers.filter((d) => d.id != updatedDriver.id);
      updatedDrivers.sort((a, b) => a.name.localeCompare(b.name));
      this.dashboard = this.dashboard.withChanges({ drivers: updatedDrivers });
    }
    if (this.dashboard && data.dispatchTask != undefined && this.dashboard.tasks) {
      const decodedDispatchTask = DispatchTask.decoder.decode(data.dispatchTask);
      if (!decodedDispatchTask.isOk()) throw new Error(decodedDispatchTask.error);
      const updatedDispatchTask = decodedDispatchTask.value;

      const updatedTasks = updatedDispatchTask.status != "completed" ?
        this.updateOneRecord(this.dashboard.tasks, updatedDispatchTask) :
        this.dashboard.tasks.filter((d) => d.id != updatedDispatchTask.id);
      this.dashboard = this.dashboard.withChanges({ tasks: updatedTasks });
    }

    if (this.dashboard && data.autogroupingStatus) {
      this.dashboard = this.dashboard.withChanges({ autogroupingStatus: data.autogroupingStatus });
    }

    if (data.debug) {
      this.onDebugEvent(data.debug);
    }

    if (this.dashboard) {
      this.dashboardUpdated.notifyListeners(this.dashboard);
    }
  }

  private async onDispatchTrackingChannelData(data: DispatchTrackingChannelData) {
    const deliveryTrackingList = data.deliveries.map((delivery) => {
      return {
        uid: delivery.deliveryUid,
        liveArrivalEstimate: delivery.secondsToDropoff == null ? undefined : new Date(Date.now() + delivery.secondsToDropoff*1000),
        liveLastUpdate: new Date(),
        routeToDropoff: delivery.routeToDropoff,
        routeToPickup: delivery.routeToPickup,
      };
    });

    this.deliveryTrackingByDriverId.set(data.driverId, deliveryTrackingList);
    this.driverTrackingUpdated.notifyListeners({
      driverId: data.driverId,
      latitude: data.latitude,
      longitude: data.longitude,
      receivedAt: new Date(data.receivedAt),
      deliveryTrackingList: deliveryTrackingList
    });
  }

  private onDebugEvent(debugAction: string) {
    switch (debugAction) {
    case "check_in":
      this.dashboardSubscription?.perform("check_in", {
        version: `${import.meta.env.VITE_APP_VERSION}`,
        title: document.title,
        path: `${window.location.pathname}${window.location.search}`,
        dashboardChannel: {
          lastConnectedAt: this.dashboardConnectedAt,
          lastDisconnectedAt: this.dashboardDisconnectedAt,
          lastReceivedDataAt: this.dashboardReceivedDataAt,
        },
        trackingChannel: {
          lastConnectedAt: this.trackingConnectedAt,
          lastDisconnectedAt: this.trackingDisconnectedAt,
          lastReceivedDataAt: this.trackingReceivedDataAt,
        },
      });
      break;

    case "capture":
      Sentry.captureException('Dumping dashboard breadcrumbs as requested');
      break;
    }
  }

  deliveryTrackingForDriver(driverId: number): DeliveryTracking[] {
    return this.deliveryTrackingByDriverId.get(driverId) ?? [];
  }
}

export class ReadonlyClient implements IDispatchClient {
  areaId: number;
  csrfToken: string;
  dashboard?: Dashboard;
  connected: boolean = true;
  deliveryTrackingByDriverId: Map<number, DeliveryTracking[]> = new Map();

  readonly dashboardConnected = new Signal<boolean>();
  readonly dashboardUpdated = new Signal<Dashboard>();
  readonly driverTrackingUpdated = new Signal<DriverTrackingUpdate>();

  constructor(areaId: number, csrfToken: string, dashboard: Dashboard, trackingData: Map<number, DeliveryTracking[]>) {
    this.areaId = areaId;
    this.csrfToken = csrfToken;
    this.dashboard = dashboard;
    this.deliveryTrackingByDriverId = trackingData;
  }

  start() {}
  stop() {}

  async updateDispatcher() : Promise<void> {}
  async updatePanic(_panicTo: string) : Promise<void> {}
  deliveryTrackingForDriver(driverId: number) : DeliveryTracking[] {
    return this.deliveryTrackingByDriverId.get(driverId) ?? [];
  }
  async allocateDeliveryToDriver(_deliveryId: string, _driverId: number, _fromDriverId: number | null) : Promise<void> {}
  async deallocateDeliveryFromDriver(_deliveryId: string, _fromDriverId: number) : Promise<void> {}
  async allocateDeliveryToGroup(_deliveryId: string, _groupId: number, _fromDriverId: number | null) : Promise<void> {}
  async removeDeliveryFromGroup(_deliveryId: string, _groupId: number) : Promise<void> {}
  async allocateGroupToDriver(_groupId: number, _driverId: number) : Promise<void> {}
  async lockGroup(_groupId: number) : Promise<void> {}
  async unlockGroup(_groupId: number) : Promise<void> {}
  async mergeGroups(_fromGroupId: number, _toGroupId: number) : Promise<void> {}
  async unGroup(_groupId: number) : Promise<void> {}
  async compactGroups() : Promise<void> {}
  async orderGroupsChronologically() : Promise<void> {}
  async triggerAutogrouping() : Promise<void> {}
  async cancelAutogrouping() : Promise<void> {}
  async sendDelayMessage(_deliveryId: string, _delay: string, _reason: string) : Promise<void> {};
  async delayPrep(_deliveryId: string, _delay: string) : Promise<void> {}
  async updateDashNote(_deliveryId: string, _note: string) : Promise<void> {}
  async addDriver(_driverId: number) : Promise<void> {}
  async removeDriver(_driverId: number) : Promise<void> {}
  async createDriverNote(_driverId: number, _note: string) : Promise<void> {}
}

export class DashboardConfig {
  apiClient: IDispatchClient;

  constructor(apiClient: IDispatchClient) {
    this.apiClient = apiClient;
  }
}

class MissingDashboardConfig extends Error {}
export const DashboardContext = createContext<DashboardConfig | null>(null);
export const useDashboardConfig = (): DashboardConfig => {
  const maybeConfig = useContext(DashboardContext);

  if (!maybeConfig) {
    throw new MissingDashboardConfig();
  }

  return maybeConfig;
}
